import { useState, useEffect, useRef } from 'react';
import { 
  Leaf, 
  Globe, 
  Server, 
  FileText, 
  ExternalLink, 
  Plus,
  Trash2,
  Loader2,
  Check,
  AlertCircle,
  Lock,
  FileCheck,
  Link as LinkIcon,
  Upload,
  Info,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Shield,
  Eye,
  Clock,
  CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MAX_EVIDENCE_PER_PLATFORM = 15;

const EVIDENCE_CATEGORIES = [
  { value: 'certification', label: 'Certification (ISO, Carbon Trust, etc.)' },
  { value: 'report', label: 'Report (ESG, Sustainability Report)' },
  { value: 'audit', label: 'Third-Party Audit' },
  { value: 'statement', label: 'Official Statement' }
];

const EVIDENCE_SCOPES = [
  { value: 'organisation-wide', label: 'Organisation-wide' },
  { value: 'platform-specific', label: 'Platform-specific' }
];

export const VendorSustainabilityModule = ({ platformId, token }) => {
  const [access, setAccess] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regions, setRegions] = useState([]);
  const [showEvidenceDialog, setShowEvidenceDialog] = useState(false);
  const [evidenceType, setEvidenceType] = useState('url'); // url or file

  const [fields, setFields] = useState({
    infrastructure_providers: '',
    regions_of_operation: [],
    regions_other: '',
    sustainability_commitments: '',
    carbon_reporting_available: null,
    uses_offsets: null,
    offset_description: ''
  });

  const [newEvidence, setNewEvidence] = useState({
    certificate_name: '',
    category: 'certification',
    issuing_organisation: '',
    date_issued: '',
    scope: 'organisation-wide',
    external_url: ''
  });

  const [showGuide, setShowGuide] = useState(true);
  const [evidenceMode, setEvidenceMode] = useState('url'); // 'url', 'file', or 'bulk'
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Bulk upload state
  const [bulkEvidence, setBulkEvidence] = useState([
    { certificate_name: '', category: 'certification', external_url: '', issuing_organisation: '', date_issued: '', scope: 'organisation-wide' }
  ]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });

  // Get current evidence count - evidence is nested under sustainability object
  const currentEvidenceCount = data?.sustainability?.evidence?.length || 0;
  const remainingSlots = MAX_EVIDENCE_PER_PLATFORM - currentEvidenceCount;

  // Fetch access and data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check access first
        const accessRes = await fetch(`${API_URL}/api/vendor/sustainability/access`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const accessData = await accessRes.json();
        setAccess(accessData);

        // If has access, fetch sustainability data
        if (accessData.has_access) {
          const dataRes = await fetch(`${API_URL}/api/vendor/platforms/${platformId}/sustainability`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (dataRes.ok) {
            const sustainabilityData = await dataRes.json();
            setData(sustainabilityData);
            setRegions(sustainabilityData.regions_options || []);
            
            // Initialize fields from sustainability object
            const fields = sustainabilityData.sustainability?.fields || sustainabilityData.fields;
            if (fields) {
              setFields({
                infrastructure_providers: fields.infrastructure_providers || '',
                regions_of_operation: fields.regions_of_operation || [],
                regions_other: fields.regions_other || '',
                sustainability_commitments: fields.sustainability_commitments || '',
                carbon_reporting_available: fields.carbon_reporting_available,
                uses_offsets: fields.uses_offsets,
                offset_description: fields.offset_description || ''
              });
            }
          }
        }

        // Also fetch regions list
        const regionsRes = await fetch(`${API_URL}/api/sustainability/regions`);
        if (regionsRes.ok) {
          const regionsData = await regionsRes.json();
          setRegions(regionsData.regions);
        }
      } catch (err) {
        console.error('Error fetching sustainability data:', err);
        toast.error('Failed to load sustainability data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [platformId, token]);

  const handleFieldChange = (name, value) => {
    setFields(prev => ({ ...prev, [name]: value }));
  };

  const toggleRegion = (region) => {
    setFields(prev => ({
      ...prev,
      regions_of_operation: prev.regions_of_operation.includes(region)
        ? prev.regions_of_operation.filter(r => r !== region)
        : [...prev.regions_of_operation, region]
    }));
  };

  const saveFields = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/vendor/platforms/${platformId}/sustainability/fields`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(fields)
      });

      if (!response.ok) throw new Error('Failed to save');
      
      const result = await response.json();
      
      if (result.status === 'published') {
        toast.success('Sustainability information updated');
      } else {
        toast.success('Changes submitted for review');
      }
      
      // Refresh data
      const dataRes = await fetch(`${API_URL}/api/vendor/platforms/${platformId}/sustainability`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (dataRes.ok) {
        setData(await dataRes.json());
      }
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const addEvidenceUrl = async () => {
    if (!newEvidence.certificate_name || !newEvidence.external_url) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/vendor/platforms/${platformId}/sustainability/evidence/url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newEvidence)
      });

      if (!response.ok) throw new Error('Failed to add evidence');
      
      const result = await response.json();
      
      if (result.status === 'approved') {
        toast.success('Evidence added successfully');
      } else {
        toast.success('Evidence submitted for review');
      }
      
      // Reset form and close dialog
      setNewEvidence({
        certificate_name: '',
        category: 'certification',
        issuing_organisation: '',
        date_issued: '',
        scope: 'organisation-wide',
        external_url: ''
      });
      setShowEvidenceDialog(false);
      
      // Refresh data
      const dataRes = await fetch(`${API_URL}/api/vendor/platforms/${platformId}/sustainability`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (dataRes.ok) {
        setData(await dataRes.json());
      }
    } catch (err) {
      toast.error('Failed to add evidence');
    } finally {
      setSaving(false);
    }
  };

  const uploadFileEvidence = async () => {
    if (!platformId) {
      toast.error('Platform ID is missing. Please refresh the page.');
      return;
    }
    
    if (!token) {
      toast.error('Not authenticated. Please log in again.');
      return;
    }
    
    if (!newEvidence.certificate_name || !selectedFile) {
      toast.error('Please provide document name and select a file');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error('Invalid file type. Allowed: PDF, PNG, JPG');
      return;
    }

    // Validate file size (25MB)
    if (selectedFile.size > 25 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 25MB');
      return;
    }

    setSaving(true);
    setUploadProgress(10);

    try {
      // Upload file directly through backend (avoids S3 CORS issues)
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const uploadResponse = await fetch(
        `${API_URL}/api/vendor/platforms/${platformId}/sustainability/evidence/upload`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || `Upload failed: ${uploadResponse.status}`);
      }
      
      const uploadData = await uploadResponse.json();

      setUploadProgress(70);

      // Register evidence metadata
      const evidenceResponse = await fetch(
        `${API_URL}/api/vendor/platforms/${platformId}/sustainability/evidence?file_url=${encodeURIComponent(uploadData.file_url)}&file_type=${encodeURIComponent(selectedFile.type.split('/')[1])}&file_size=${uploadData.file_size}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            certificate_name: newEvidence.certificate_name,
            category: newEvidence.category,
            issuing_organisation: newEvidence.issuing_organisation || null,
            date_issued: newEvidence.date_issued || null,
            scope: newEvidence.scope
          })
        }
      );

      if (!evidenceResponse.ok) throw new Error('Failed to register evidence');
      
      const result = await evidenceResponse.json();
      setUploadProgress(100);
      
      if (result.status === 'approved') {
        toast.success('Evidence uploaded successfully');
      } else {
        toast.success('Evidence uploaded and submitted for review');
      }
      
      // Reset form
      setNewEvidence({
        certificate_name: '',
        category: 'certification',
        issuing_organisation: '',
        date_issued: '',
        scope: 'organisation-wide',
        external_url: ''
      });
      setSelectedFile(null);
      setShowEvidenceDialog(false);
      
      // Refresh data
      const dataRes = await fetch(`${API_URL}/api/vendor/platforms/${platformId}/sustainability`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (dataRes.ok) {
        setData(await dataRes.json());
      }
    } catch (err) {
      console.error('=== UPLOAD ERROR ===', err);
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const deleteEvidence = async (evidenceId) => {
    if (!confirm('Are you sure you want to delete this evidence?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/vendor/platforms/${platformId}/sustainability/evidence/${evidenceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success('Evidence removed');
      
      // Refresh data
      const dataRes = await fetch(`${API_URL}/api/vendor/platforms/${platformId}/sustainability`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (dataRes.ok) {
        setData(await dataRes.json());
      }
    } catch (err) {
      toast.error('Failed to delete evidence');
    }
  };

  // Bulk evidence handlers
  const addBulkRow = () => {
    if (bulkEvidence.length >= remainingSlots) {
      toast.error(`Maximum ${MAX_EVIDENCE_PER_PLATFORM} evidence files per platform. You have ${currentEvidenceCount} already.`);
      return;
    }
    setBulkEvidence(prev => [...prev, { 
      certificate_name: '', 
      category: 'certification', 
      external_url: '', 
      issuing_organisation: '', 
      date_issued: '', 
      scope: 'organisation-wide' 
    }]);
  };

  const removeBulkRow = (index) => {
    if (bulkEvidence.length <= 1) return;
    setBulkEvidence(prev => prev.filter((_, i) => i !== index));
  };

  const updateBulkRow = (index, field, value) => {
    setBulkEvidence(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const submitBulkEvidence = async () => {
    // Validate all rows have required fields
    const validItems = bulkEvidence.filter(item => item.certificate_name && item.external_url);
    
    if (validItems.length === 0) {
      toast.error('Please fill in at least one evidence item with name and URL');
      return;
    }

    if (currentEvidenceCount + validItems.length > MAX_EVIDENCE_PER_PLATFORM) {
      toast.error(`Adding ${validItems.length} items would exceed the limit of ${MAX_EVIDENCE_PER_PLATFORM}. You have ${remainingSlots} slots remaining.`);
      return;
    }

    setBulkUploading(true);
    setBulkProgress({ current: 0, total: validItems.length });

    try {
      const response = await fetch(`${API_URL}/api/vendor/platforms/${platformId}/sustainability/evidence/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: validItems })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload evidence');
      }

      const result = await response.json();
      setBulkProgress({ current: validItems.length, total: validItems.length });

      if (result.auto_approved) {
        toast.success(`${result.added_count} evidence items added and approved`);
      } else {
        toast.success(`${result.added_count} evidence items submitted for review`);
      }

      // Reset form
      setBulkEvidence([{ 
        certificate_name: '', 
        category: 'certification', 
        external_url: '', 
        issuing_organisation: '', 
        date_issued: '', 
        scope: 'organisation-wide' 
      }]);
      setShowEvidenceDialog(false);

      // Refresh data
      const dataRes = await fetch(`${API_URL}/api/vendor/platforms/${platformId}/sustainability`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (dataRes.ok) {
        setData(await dataRes.json());
      }
    } catch (err) {
      toast.error(err.message || 'Failed to upload evidence');
    } finally {
      setBulkUploading(false);
      setBulkProgress({ current: 0, total: 0 });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground mt-2">Loading sustainability module...</p>
        </CardContent>
      </Card>
    );
  }

  // No access - show locked state
  if (!access?.has_access) {
    return (
      <Card className="border-dashed" data-testid="sustainability-locked">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-muted-foreground" />
            <CardTitle className="text-lg">Sustainability & Environmental Impact</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Alert>
            <Lock className="w-4 h-4" />
            <AlertTitle>Module Not Enabled</AlertTitle>
            <AlertDescription>
              The Sustainability Disclosure module is not enabled for your account. 
              {access?.tier === 'enterprise' 
                ? ' Please contact support to enable this feature.'
                : ' This feature is available as an add-on for Verified and Claimed tier vendors, or included with Enterprise tier.'}
            </AlertDescription>
          </Alert>
          <p className="text-sm text-muted-foreground mt-4">
            Contact our team to enable sustainability disclosures for your platform profile.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Has access but can't edit (Claimed tier with view-only)
  if (!access?.can_edit) {
    return (
      <Card data-testid="sustainability-view-only">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Leaf className="w-5 h-5 text-green-600" />
            <CardTitle className="text-lg">Sustainability & Environmental Impact</CardTitle>
          </div>
          <CardDescription>View-only access</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="mb-4">
            <Info className="w-4 h-4" />
            <AlertDescription>
              Your account tier ({access?.tier}) has view-only access to sustainability information. 
              Upgrade to Verified or Enterprise tier to edit sustainability disclosures.
            </AlertDescription>
          </Alert>
          
          {data?.sustainability?.enabled && (
            <div className="space-y-4 text-sm">
              <p><strong>Current Status:</strong> {data.sustainability.disclosure_status}</p>
              <p><strong>Last Updated:</strong> {data.sustainability.last_updated ? new Date(data.sustainability.last_updated).toLocaleDateString() : 'Never'}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full edit access
  return (
    <Card data-testid="sustainability-module">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Leaf className="w-5 h-5 text-green-600" />
            <CardTitle className="text-lg">Sustainability & Environmental Impact</CardTitle>
          </div>
          {data?.sustainability?.disclosure_status && (
            <Badge variant={data.sustainability.disclosure_status === 'disclosed' ? 'default' : 'secondary'}>
              {data.sustainability.disclosure_status === 'disclosed' ? 'Disclosed' : 
               data.sustainability.disclosure_status === 'limited' ? 'Limited' : 'Not Disclosed'}
            </Badge>
          )}
        </div>
        <CardDescription>
          Provide factual, verifiable information about your platform's environmental impact
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Status Indicators */}
        {data?.pending_edit && (
          <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800">
            <Clock className="w-4 h-4 text-amber-600" />
            <AlertTitle className="text-amber-800 dark:text-amber-400">Pending Review</AlertTitle>
            <AlertDescription className="text-amber-700 dark:text-amber-300 text-sm">
              Your sustainability update was submitted on {new Date(data.pending_edit.created_at).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric'
              })} and is awaiting admin review.
            </AlertDescription>
          </Alert>
        )}

        {data?.last_rejection && !data?.pending_edit && (
          <Alert className="bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <AlertTitle className="text-red-800 dark:text-red-400">Changes Requested</AlertTitle>
            <AlertDescription className="text-red-700 dark:text-red-300 text-sm">
              <p className="mb-2">Your last submission requires changes:</p>
              <div className="bg-red-100 dark:bg-red-900/30 rounded p-3 text-sm">
                {data.last_rejection.admin_notes || "Please review your submission and ensure all information is factual and verifiable."}
              </div>
              <p className="text-xs mt-2 text-red-600 dark:text-red-400">
                Feedback received: {new Date(data.last_rejection.reviewed_at).toLocaleDateString('en-GB', {
                  day: 'numeric', month: 'short', year: 'numeric'
                })}
              </p>
            </AlertDescription>
          </Alert>
        )}

        {data?.sustainability?.enabled && data?.sustainability?.disclosure_status !== 'none' && !data?.pending_edit && !data?.last_rejection && (
          <Alert className="bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <AlertTitle className="text-green-800 dark:text-green-400">Live</AlertTitle>
            <AlertDescription className="text-green-700 dark:text-green-300 text-sm">
              Your sustainability disclosure is live and visible to users.
              {data.sustainability.last_reviewed_at && (
                <span className="block text-xs mt-1">
                  Last approved: {new Date(data.sustainability.last_reviewed_at).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'short', year: 'numeric'
                  })}
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}


        {/* Onboarding Guide - Collapsible */}
        <Collapsible open={showGuide} onOpenChange={setShowGuide}>
          <Card className="border-green-200/50 bg-green-50/30 dark:bg-green-950/10">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-green-100/30 transition-colors rounded-t-lg pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-green-600" />
                    <CardTitle className="text-base">Getting Started with Sustainability Disclosures</CardTitle>
                  </div>
                  {showGuide ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-4">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-green-700 dark:text-green-400">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm text-foreground">Fill in the basics</p>
                      <p className="text-xs text-muted-foreground">Infrastructure, regions, and commitments</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-green-700 dark:text-green-400">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm text-foreground">Add evidence</p>
                      <p className="text-xs text-muted-foreground">Link to certifications and reports</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-green-700 dark:text-green-400">3</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm text-foreground">Submit for review</p>
                      <p className="text-xs text-muted-foreground">Admin approves before publishing</p>
                    </div>
                  </div>
                </div>

                <Separator className="bg-green-200/50" />

                <div className="grid sm:grid-cols-2 gap-4 text-xs">
                  <div className="space-y-2">
                    <p className="font-medium text-foreground flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5 text-green-600" />
                      What we display
                    </p>
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• All info labelled as &quot;vendor-provided&quot;</li>
                      <li>• Disclaimer that GoCompareAI doesn&apos;t verify</li>
                      <li>• Your disclosure status (none/limited/disclosed)</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <p className="font-medium text-foreground flex items-center gap-1.5">
                      <Eye className="w-3.5 h-3.5 text-green-600" />
                      What users see
                    </p>
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• Section on your platform detail page</li>
                      <li>• Green leaf icon indicator</li>
                      <li>• Links to your certifications/reports</li>
                    </ul>
                  </div>
                </div>

                <Alert className="bg-white/50 dark:bg-background/50 border-green-200/50">
                  <Info className="w-4 h-4 text-green-600" />
                  <AlertDescription className="text-xs">
                    <strong>Tip:</strong> Start with infrastructure and regions — these are factual and easy to provide. 
                    Add certifications later for maximum credibility.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Helper Notice */}
        <Alert className="bg-muted/50">
          <Info className="w-4 h-4" />
          <AlertDescription className="text-xs">
            Provide factual, verifiable information only. Marketing claims are not permitted. 
            All information will be clearly labelled as vendor-provided.
          </AlertDescription>
        </Alert>

        {/* Infrastructure Providers */}
        <div className="space-y-2">
          <Label htmlFor="infrastructure_providers" className="flex items-center gap-2">
            <Server className="w-4 h-4 text-muted-foreground" />
            Primary Infrastructure Provider(s)
          </Label>
          <Input
            id="infrastructure_providers"
            placeholder="e.g., AWS, Google Cloud, Azure, On-premise"
            value={fields.infrastructure_providers}
            onChange={(e) => handleFieldChange('infrastructure_providers', e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            List the cloud providers or data center operators hosting your platform
          </p>
        </div>

        {/* Regions of Operation */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-muted-foreground" />
            Regions of Operation
          </Label>
          <div className="flex flex-wrap gap-2">
            {regions.map((region) => (
              <Badge
                key={region}
                variant={fields.regions_of_operation.includes(region) ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => toggleRegion(region)}
              >
                {fields.regions_of_operation.includes(region) && <Check className="w-3 h-3 mr-1" />}
                {region}
              </Badge>
            ))}
          </div>
          <Input
            placeholder="Other region (specify)"
            value={fields.regions_other}
            onChange={(e) => handleFieldChange('regions_other', e.target.value)}
            className="mt-2"
          />
        </div>

        {/* Sustainability Commitments */}
        <div className="space-y-2">
          <Label htmlFor="sustainability_commitments" className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-muted-foreground" />
            Sustainability or Environmental Commitments
          </Label>
          <Textarea
            id="sustainability_commitments"
            placeholder="Describe your organization's sustainability commitments, goals, and initiatives..."
            value={fields.sustainability_commitments}
            onChange={(e) => handleFieldChange('sustainability_commitments', e.target.value)}
            rows={4}
          />
        </div>

        {/* Carbon Reporting Toggle */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="space-y-0.5">
            <Label>Carbon Reporting Available to Customers</Label>
            <p className="text-xs text-muted-foreground">
              Do you provide carbon footprint data or reports to your customers?
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {fields.carbon_reporting_available === null ? 'Not set' : 
               fields.carbon_reporting_available ? 'Yes' : 'No'}
            </span>
            <Switch
              checked={fields.carbon_reporting_available === true}
              onCheckedChange={(checked) => handleFieldChange('carbon_reporting_available', checked)}
            />
          </div>
        </div>

        {/* Offsets Toggle */}
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-0.5">
              <Label>Use of Offsets or Mitigation Measures</Label>
              <p className="text-xs text-muted-foreground">
                Does your organization use carbon offsets or other mitigation measures?
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {fields.uses_offsets === null ? 'Not set' : 
                 fields.uses_offsets ? 'Yes' : 'No'}
              </span>
              <Switch
                checked={fields.uses_offsets === true}
                onCheckedChange={(checked) => handleFieldChange('uses_offsets', checked)}
              />
            </div>
          </div>
          
          {fields.uses_offsets && (
            <Textarea
              placeholder="Describe your offset or mitigation measures..."
              value={fields.offset_description}
              onChange={(e) => handleFieldChange('offset_description', e.target.value)}
              rows={3}
            />
          )}
        </div>

        {/* Save Fields Button */}
        <Button 
          onClick={saveFields} 
          disabled={saving}
          className="w-full"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Save Sustainability Information
            </>
          )}
        </Button>

        <Separator />

        {/* Evidence & Certifications */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-muted-foreground" />
                Evidence & Certifications
                <Badge variant="outline" className="ml-2 text-xs">
                  {currentEvidenceCount}/{MAX_EVIDENCE_PER_PLATFORM}
                </Badge>
              </h4>
              <p className="text-xs text-muted-foreground mt-1">
                Upload certifications, reports, or link to official documents
                {remainingSlots < 5 && remainingSlots > 0 && (
                  <span className="text-amber-600 ml-1">({remainingSlots} slots remaining)</span>
                )}
                {remainingSlots === 0 && (
                  <span className="text-red-600 ml-1">(Maximum reached)</span>
                )}
              </p>
            </div>
            
            <Dialog open={showEvidenceDialog} onOpenChange={(open) => {
              setShowEvidenceDialog(open);
              if (!open) {
                // Reset states when closing
                setEvidenceMode('url');
                setBulkEvidence([{ 
                  certificate_name: '', 
                  category: 'certification', 
                  external_url: '', 
                  issuing_organisation: '', 
                  date_issued: '', 
                  scope: 'organisation-wide' 
                }]);
              }
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={remainingSlots === 0}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Evidence
                </Button>
              </DialogTrigger>
              <DialogContent className={cn("max-w-lg", evidenceMode === 'bulk' && "max-w-2xl")}>
                <DialogHeader>
                  <DialogTitle>Add Sustainability Evidence</DialogTitle>
                  <DialogDescription>
                    Upload a file, link to documents, or add multiple items at once
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Evidence Type Toggle */}
                  <div className="flex gap-1 p-1 bg-muted rounded-lg">
                    <button
                      className={cn(
                        "flex-1 py-2 px-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5",
                        evidenceMode === 'url' 
                          ? "bg-background shadow text-foreground" 
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={() => setEvidenceMode('url')}
                    >
                      <LinkIcon className="w-4 h-4" />
                      Single URL
                    </button>
                    <button
                      className={cn(
                        "flex-1 py-2 px-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5",
                        evidenceMode === 'file' 
                          ? "bg-background shadow text-foreground" 
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={() => setEvidenceMode('file')}
                    >
                      <Upload className="w-4 h-4" />
                      Upload File
                    </button>
                    <button
                      className={cn(
                        "flex-1 py-2 px-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5",
                        evidenceMode === 'bulk' 
                          ? "bg-background shadow text-foreground" 
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={() => setEvidenceMode('bulk')}
                    >
                      <Plus className="w-4 h-4" />
                      Bulk Add
                    </button>
                  </div>

                  {/* Bulk Mode UI */}
                  {evidenceMode === 'bulk' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          Add multiple evidence items at once ({bulkEvidence.length} item{bulkEvidence.length !== 1 ? 's' : ''})
                        </p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={addBulkRow}
                          disabled={bulkEvidence.length >= remainingSlots}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Row
                        </Button>
                      </div>

                      <div className="max-h-[400px] overflow-y-auto space-y-3 pr-2">
                        {bulkEvidence.map((item, index) => (
                          <div key={index} className="border rounded-lg p-3 space-y-3 bg-muted/30">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-muted-foreground">Item {index + 1}</span>
                              {bulkEvidence.length > 1 && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                                  onClick={() => removeBulkRow(index)}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                placeholder="Document name *"
                                value={item.certificate_name}
                                onChange={(e) => updateBulkRow(index, 'certificate_name', e.target.value)}
                                className="text-sm"
                              />
                              <Input
                                placeholder="URL *"
                                value={item.external_url}
                                onChange={(e) => updateBulkRow(index, 'external_url', e.target.value)}
                                className="text-sm"
                              />
                            </div>
                            
                            <div className="grid grid-cols-3 gap-2">
                              <Select 
                                value={item.category}
                                onValueChange={(value) => updateBulkRow(index, 'category', value)}
                              >
                                <SelectTrigger className="text-sm">
                                  <SelectValue placeholder="Category" />
                                </SelectTrigger>
                                <SelectContent>
                                  {EVIDENCE_CATEGORIES.map(cat => (
                                    <SelectItem key={cat.value} value={cat.value}>
                                      {cat.label.split(' ')[0]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Input
                                placeholder="Issuer (optional)"
                                value={item.issuing_organisation}
                                onChange={(e) => updateBulkRow(index, 'issuing_organisation', e.target.value)}
                                className="text-sm"
                              />
                              <Select 
                                value={item.scope}
                                onValueChange={(value) => updateBulkRow(index, 'scope', value)}
                              >
                                <SelectTrigger className="text-sm">
                                  <SelectValue placeholder="Scope" />
                                </SelectTrigger>
                                <SelectContent>
                                  {EVIDENCE_SCOPES.map(scope => (
                                    <SelectItem key={scope.value} value={scope.value}>
                                      {scope.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        ))}
                      </div>

                      {bulkProgress.total > 0 && (
                        <div className="space-y-2">
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary transition-all duration-300"
                              style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground text-center">
                            Uploading {bulkProgress.current} of {bulkProgress.total}...
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Single URL/File Mode */}
                  {evidenceMode !== 'bulk' && (
                    <>
                      <div className="space-y-2">
                        <Label>Document Name *</Label>
                        <Input
                          placeholder="e.g., ISO 14001 Certification"
                          value={newEvidence.certificate_name}
                          onChange={(e) => setNewEvidence(prev => ({ ...prev, certificate_name: e.target.value }))}
                        />
                      </div>
                  
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select 
                      value={newEvidence.category}
                      onValueChange={(value) => setNewEvidence(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EVIDENCE_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Issuing Organisation</Label>
                    <Input
                      placeholder="e.g., ISO, Carbon Trust, BSI"
                      value={newEvidence.issuing_organisation}
                      onChange={(e) => setNewEvidence(prev => ({ ...prev, issuing_organisation: e.target.value }))}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date Issued</Label>
                      <Input
                        type="date"
                        value={newEvidence.date_issued}
                        onChange={(e) => setNewEvidence(prev => ({ ...prev, date_issued: e.target.value }))}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Scope</Label>
                      <Select 
                        value={newEvidence.scope}
                        onValueChange={(value) => setNewEvidence(prev => ({ ...prev, scope: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EVIDENCE_SCOPES.map(scope => (
                            <SelectItem key={scope.value} value={scope.value}>
                              {scope.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* URL Mode */}
                  {evidenceMode === 'url' && (
                    <div className="space-y-2">
                      <Label>Document URL *</Label>
                      <Input
                        type="url"
                        placeholder="https://example.com/sustainability-report.pdf"
                        value={newEvidence.external_url}
                        onChange={(e) => setNewEvidence(prev => ({ ...prev, external_url: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground">
                        Link to the official document or certification
                      </p>
                    </div>
                  )}

                  {/* File Upload Mode */}
                  {evidenceMode === 'file' && (
                    <div className="space-y-2">
                      <Label>Upload Document *</Label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setSelectedFile(file);
                          }
                        }}
                      />
                      <div 
                        className={cn(
                          "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                          selectedFile 
                            ? "border-primary bg-primary/5" 
                            : "border-muted-foreground/25 hover:border-primary/50"
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        {selectedFile ? (
                          <div className="space-y-1">
                            <FileCheck className="w-8 h-8 mx-auto text-primary" />
                            <p className="text-sm font-medium">{selectedFile.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">
                              Click to select file
                            </p>
                            <p className="text-xs text-muted-foreground">
                              PDF, PNG, JPG (max 25MB)
                            </p>
                          </div>
                        )}
                      </div>
                      {uploadProgress > 0 && (
                        <div className="space-y-1">
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary transition-all duration-300"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground text-center">
                            Uploading... {uploadProgress}%
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                    </>
                  )}
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => {
                    setShowEvidenceDialog(false);
                    setSelectedFile(null);
                    setUploadProgress(0);
                    setBulkEvidence([{ 
                      certificate_name: '', 
                      category: 'certification', 
                      external_url: '', 
                      issuing_organisation: '', 
                      date_issued: '', 
                      scope: 'organisation-wide' 
                    }]);
                  }}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => {
                      if (evidenceMode === 'url') {
                        addEvidenceUrl();
                      } else if (evidenceMode === 'file') {
                        uploadFileEvidence();
                      } else if (evidenceMode === 'bulk') {
                        submitBulkEvidence();
                      }
                    }} 
                    disabled={saving || bulkUploading}
                  >
                    {(saving || bulkUploading) ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : evidenceMode === 'url' ? (
                      <Plus className="w-4 h-4 mr-2" />
                    ) : evidenceMode === 'bulk' ? (
                      <Plus className="w-4 h-4 mr-2" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {evidenceMode === 'url' ? 'Add Link' : 
                     evidenceMode === 'bulk' ? `Add ${bulkEvidence.filter(i => i.certificate_name && i.external_url).length} Items` :
                     'Upload File'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Evidence List */}
          {data?.sustainability?.evidence && data.sustainability.evidence.length > 0 ? (
            <div className="space-y-3">
              {data.sustainability.evidence.map((item) => (
                <div 
                  key={item.id}
                  className={cn(
                    "border rounded-lg p-4 flex items-start justify-between gap-4",
                    item.status === 'pending' && "border-amber-500/50 bg-amber-50/30"
                  )}
                >
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{item.certificate_name}</span>
                      <Badge variant="outline" className="text-xs">
                        {EVIDENCE_CATEGORIES.find(c => c.value === item.category)?.label || item.category}
                      </Badge>
                      {item.status === 'pending' && (
                        <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                          Pending Review
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      {item.issuing_organisation && <p>Issuer: {item.issuing_organisation}</p>}
                      {item.date_issued && <p>Issued: {item.date_issued}</p>}
                      <p>Scope: {item.scope === 'platform-specific' ? 'Platform-specific' : 'Organisation-wide'}</p>
                    </div>
                    {item.file_url && item.file_type === 'url' && (
                      <a 
                        href={item.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline flex items-center gap-1 mt-2"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View Document
                      </a>
                    )}
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => deleteEvidence(item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-dashed rounded-lg p-8 text-center">
              <FileCheck className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                No evidence or certifications added yet
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Add certifications, reports, or links to official sustainability documents
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default VendorSustainabilityModule;
